/**
 * 遷移が固まったときに「何が起きていたか」を後から言えるようにするための計測（PRODUCT_DEV-612）。
 *
 * この事象は **例外が飛ばず、Network タブにも痕跡が残らない**（Service Worker が
 * Cache Storage から即座に応答するとネットワークリクエスト自体が発生しない）。
 * そのため一時的な `console.log` を仕込んで再現を待つ、というやり方では毎回同じ苦労をする。
 * 常設の軽量な計測をここに置き、`navigationWatchdog` が発火したときにまとめて吐く。
 *
 * ## 何を残すか
 *
 * - **進行中の lazy チャンク**: ウォッチドッグ発火時にこれが空でなければ原因はチャンク読み込み。
 *   空なら遷移先のサスペンド以外（別の原因）を疑う、という切り分けができる。
 * - **パンくず**: 直近の遷移まわりのイベント列。自由記録と振り返りで何が違うのかを、
 *   ログを仕込み直さずに比較できる。
 *
 * 平常時は何も出力しない（リングバッファに積むだけ）。
 */

export interface Breadcrumb {
  /** ページ読み込みからの経過ミリ秒（`performance.now()`）。 */
  at: number;
  event: string;
  detail?: Record<string, unknown>;
}

/** 保持するパンくずの数。遷移1回分を追えれば十分なので小さくてよい。 */
const MAX_BREADCRUMBS = 40;

const breadcrumbs: Breadcrumb[] = [];

/** 遷移まわりの出来事を記録する（出力はしない）。 */
export function breadcrumb(
  event: string,
  detail?: Record<string, unknown>,
): void {
  breadcrumbs.push({ at: Math.round(performance.now()), event, detail });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...breadcrumbs];
}

/** テスト用。 */
export function clearBreadcrumbs(): void {
  breadcrumbs.length = 0;
}

/** 読み込み中の lazy チャンク: チャンク名 → 開始時刻（`performance.now()`）。 */
const pendingChunks = new Map<string, number>();

export function markChunkStart(name: string): void {
  pendingChunks.set(name, performance.now());
  breadcrumb("chunk:start", { name });
}

export function markChunkSettled(
  name: string,
  outcome: "loaded" | "failed" | "timeout",
): void {
  const startedAt = pendingChunks.get(name);
  pendingChunks.delete(name);
  breadcrumb(`chunk:${outcome}`, {
    name,
    ms: startedAt === undefined ? null : Math.round(performance.now() - startedAt),
  });
}

/** まだ解決していない lazy チャンクと、その経過時間。 */
export function getPendingChunks(): Array<{ name: string; ms: number }> {
  const now = performance.now();
  return [...pendingChunks].map(([name, startedAt]) => ({
    name,
    ms: Math.round(now - startedAt),
  }));
}

/** テスト用。 */
export function clearPendingChunks(): void {
  pendingChunks.clear();
}

/**
 * react-router 自身が持つナビゲーション状態のスナップショット。
 *
 * **フック（`useLocation` / `useNavigation`）では取れない。** 遷移がコミットされない
 * ときはフックの値も更新されないため、「React 側が遅れているのか、react-router 側が
 * まだ解決していないのか」をフック経由では区別できない。router インスタンスの
 * `router.state` は React のコミットとは無関係に同期更新されるので、そちらを命令的に読む。
 */
export interface RouterStateSnapshot {
  /** `router.state.navigation.state`: "idle" | "loading" | "submitting"。 */
  navigationState: string;
  /** 解決中の遷移先（`navigation.location`）。idle なら null。 */
  navigatingTo: string | null;
  /** react-router がコミット済みとみなしている location。 */
  routerLocation: string;
  historyAction: string | null;
}

type RouterStateReader = () => RouterStateSnapshot;

let readRouterState: RouterStateReader | null = null;

/** router.tsx から router インスタンスの読み取り口を登録する（循環 import を避けるため注入式）。 */
export function setRouterStateReader(reader: RouterStateReader): void {
  readRouterState = reader;
}

/** 登録されていれば react-router の現在状態を返す。未登録・失敗時は null。 */
export function getRouterState(): RouterStateSnapshot | null {
  try {
    return readRouterState?.() ?? null;
  } catch {
    return null;
  }
}

/** テスト用。 */
export function clearRouterStateReader(): void {
  readRouterState = null;
}

/** 診断レポート1本の上限。RUM のイベントサイズと Console の可読性の両方に効く。 */
export const DIAGNOSTIC_MAX_CHARS = 4_000;

/**
 * 診断情報を JSON 文字列にする。
 *
 * **オブジェクトのまま `console.error` に渡すと、フルページロード後に参照が死んで
 * 展開できなくなる**（Chrome は "No properties" と表示する）。ウォッチドッグは
 * まさにフルページロードして復帰するので、報告は必ず文字列で出す必要がある。
 * RUM も `Error` に生やした任意プロパティを捨てるため、同じ理由で文字列が要る。
 */
export function safeStringify(
  value: unknown,
  maxChars = DIAGNOSTIC_MAX_CHARS,
): string {
  let json: string;
  try {
    const seen = new WeakSet<object>();
    json =
      JSON.stringify(value, (_key, val) => {
        if (typeof val === "object" && val !== null) {
          if (seen.has(val)) return "[circular]";
          seen.add(val);
        }
        return val;
      }) ?? "";
  } catch {
    return "[unserializable]";
  }
  return json.length > maxChars
    ? `${json.slice(0, maxChars)}…[truncated]`
    : json;
}
